import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiFileTextLight,
  PiMagnifyingGlassLight,
  PiSlidersHorizontalLight,
  PiInfoLight,
} from 'react-icons/pi';

import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { ClientSelectorClient } from '../shared/ClientSelector';
import MatterContactsField from './MatterContactsField';
import { Collaborator } from '../shared/CollaboratorSelector';
import FileOrDescriptionSelector from '../shared/FileOrDescriptionSelector';
import IndexSourceSelector from './IndexSourceSelector';
import WizardToggleRow from './WizardToggleRow';
import { hasCasesSupport } from './matterConstants';
import {
  BaseForm,
  BaseFormFieldConfig,
  BaseFormSectionConfig,
  WizardStepRail,
} from '../shared/FormComponents';
import { Field, FieldGrid } from '../shared/brand/Field';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { useAppDispatch } from '../../store';
import { isMatterEconomicsEnabled } from '../../utils/config';
import WizardBillingFields from './WizardBillingFields';
import {
  MatterPricingValue,
  emptyMatterPricing,
  getMatterPricingErrors,
} from './economics/economicsConstants';
import { showModal, hideModal } from '../../store/actions/modalActions';
import {
  PiScalesLight,
  PiGavelLight,
  PiDatabaseLight,
  PiListChecksLight,
  PiChatTextLight,
  PiTextAlignLeftLight,
  PiNoteLight,
  PiSparkleLight,
  PiTrashLight,
} from 'react-icons/pi';
import { WizardStepTip, FeatureTour, matterWizardStepTips, matterWizardTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { hasCategoryGroups } from './matterCategories';

// ─── Types ───────────────────────────────────────────────────────────

type WizardStep = 'basics' | 'documents' | 'research' | 'review';

interface AnalysisOptionsValue {
  fetchLaws: boolean;
  fetchPrecedents: boolean;
  fetchDataSource: boolean;
  createTasks: boolean;
  createQuestionnaire: boolean;
  generateDescription: boolean;
  generateNotes: boolean;
  generateDraftSuggestions: boolean;
  generateInsightSuggestions: boolean;
}

const DEFAULT_ANALYSIS_OPTIONS: AnalysisOptionsValue = {
  fetchLaws: true,
  fetchPrecedents: true,
  fetchDataSource: true,
  createTasks: true,
  createQuestionnaire: true,
  generateDescription: true,
  generateNotes: true,
  generateDraftSuggestions: true,
  generateInsightSuggestions: true,
};

export interface MatterWizardData {
  matterTitle: string;
  category: string;
  clients: ClientSelectorClient[];
  collaborators: Collaborator[];
  filesOrDescription: { files: File[]; description: string };
  language: string;
  jurisdiction: string;
  selectedIndexes: string[];
  dataSources: string[];
  analysisOptions: AnalysisOptionsValue;
  projectUUID?: string;
  pricing?: MatterPricingValue;
}

/**
 * The org's data sources as switchable rows - the same rows the legal sources
 * use, so the two lists on the Sources step read as one choice rather than a
 * card grid above a multi-select dropdown.
 */
const DataSourceToggleList: React.FC<{
  value?: string[];
  onChange: (v: string[]) => void;
  options: DropdownOption[];
  disabled?: boolean;
}> = ({ value, onChange, options, disabled }) => {
  const selected = new Set(value || []);
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((opt) => (
        <WizardToggleRow
          key={String(opt.value)}
          label={String(opt.label)}
          enabled={selected.has(String(opt.value))}
          onToggle={() => toggle(String(opt.value))}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

interface MatterWizardProps {
  initialTitle?: string;
  categories: DropdownOption[];
  languages: DropdownOption[];
  jurisdictions: DropdownOption[];
  clientOptions: DropdownOption[];
  clientRoleOptions: DropdownOption[];
  dataSourceOptions: DropdownOption[];
  projectOptions?: DropdownOption[];
  defaultLanguage: string;
  defaultJurisdiction: string;
  /** Org/user default currency used to seed the pricing section (US #2250). */
  defaultCurrency?: string;
  autoAddClient: { ClientID: string; ClientRoles: string[] } | null;
  onAddNewClient: () => void;
  onSubmit: (data: MatterWizardData) => void;
  onCancel: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

// ─── Step definitions ────────────────────────────────────────────────

/** Pinned so the canvas cannot resize as the user moves between steps. */
const WIZARD_MAX_WIDTH = 'max-w-3xl';

const STEPS: { key: WizardStep; label: string; icon: React.ReactNode }[] = [
  { key: 'basics', label: 'Details', icon: <PiInfoLight className="w-4 h-4" /> },
  { key: 'documents', label: 'Documents', icon: <PiFileTextLight className="w-4 h-4" /> },
  { key: 'research', label: 'Sources', icon: <PiMagnifyingGlassLight className="w-4 h-4" /> },
  { key: 'review', label: 'Review and create', icon: <PiSlidersHorizontalLight className="w-4 h-4" /> },
];

// ─── Review step: custom components for BaseForm ─────────────────────
//
// The review step is three ordinary BaseForm sections - Summary, Legal
// research, PONS generation - so its headings are the same size and weight as
// every other step's. They used to be GlassCards with their own title style,
// the one step that looked different.

interface ReviewSummaryValue {
  title: string;
  docCount: number;
  jurisdictionLabel: string;
  languageLabel: string;
  clientCount: number;
  collaboratorCount: number;
  indexCount: number;
  dataSourceCount: number;
  category: string;
}

const ReviewSummary: React.FC<{ summary: ReviewSummaryValue; showDataSources: boolean }> = ({
  summary,
  showDataSources,
}) => {
  const { t } = useTranslation();
  const count = (n: number, one: string, many: string) =>
    n === 0 ? '' : n === 1 ? t(one) : t(many, { count: n });
  return (
    <FieldGrid columns={3}>
      <Field label={t('Title')} value={summary.title} className="sm:col-span-3" />
      {summary.category && <Field label={t('Category')} value={summary.category} />}
      <Field
        label={t('Documents')}
        value={summary.docCount > 0
          ? count(summary.docCount, '1 file', '{{count}} files')
          : t('Text description')}
      />
      {/* Unset is a valid answer; Field renders it as a muted "Not set". */}
      <Field label={t('Jurisdiction')} value={summary.jurisdictionLabel} />
      <Field label={t('Language')} value={summary.languageLabel} />
      <Field
        label={t('Legal sources')}
        value={count(summary.indexCount, '1 source', '{{count}} sources')}
        emptyText={t('None selected')}
      />
      {showDataSources && (
        <Field
          label={t('Data sources')}
          value={count(summary.dataSourceCount, '1 data source', '{{count}} data sources')}
          emptyText={t('None selected')}
        />
      )}
      <Field
        label={t('Contacts')}
        value={count(summary.clientCount, '1 contact', '{{count}} contacts')}
        emptyText={t('None')}
      />
      <Field
        label={t('Access')}
        value={count(summary.collaboratorCount, '1 person', '{{count}} people')}
        emptyText={t('Only you')}
      />
    </FieldGrid>
  );
};

type AnalysisOptionGroup = 'research' | 'generation';

interface AnalysisOptionTogglesProps {
  value: AnalysisOptionsValue;
  onChange: (val: AnalysisOptionsValue) => void;
  group: AnalysisOptionGroup;
  jurisdiction: string;
  hasDataSources: boolean;
}

const AnalysisOptionToggles: React.FC<AnalysisOptionTogglesProps> = ({
  value,
  onChange,
  group,
  jurisdiction,
  hasDataSources,
}) => {
  const { t } = useTranslation();
  const opts = value || DEFAULT_ANALYSIS_OPTIONS;
  const showCases = hasCasesSupport(jurisdiction);

  type ToggleOpt = { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode };

  const researchOpts: ToggleOpt[] = [
    { key: 'fetchLaws', label: t('Applicable laws'), description: t('Search for statutes, regulations, and legal provisions relevant to your matter.'), icon: <PiScalesLight /> },
    ...(showCases ? [{ key: 'fetchPrecedents' as const, label: t('Precedents / Case law'), description: t('Find court rulings and judicial decisions that may apply to your matter.'), icon: <PiGavelLight /> }] : []),
    ...(hasDataSources ? [{ key: 'fetchDataSource' as const, label: t('Connected data sources'), description: t('Search your own uploaded document collections for relevant information.'), icon: <PiDatabaseLight /> }] : []),
  ];

  const genOpts: ToggleOpt[] = [
    { key: 'createTasks', label: t('Suggested tasks'), description: t('Generate a prioritized action plan with recommended next steps.'), icon: <PiListChecksLight /> },
    { key: 'createQuestionnaire', label: t('Client questionnaire'), description: t('Create a tailored set of questions to gather key information from your client.'), icon: <PiChatTextLight /> },
    { key: 'generateDescription', label: t('Matter description'), description: t('Produce a concise summary describing the scope and context of the matter.'), icon: <PiTextAlignLeftLight /> },
    { key: 'generateNotes', label: t('Case notes'), description: t('Extract key facts, dates, and observations into structured case notes.'), icon: <PiNoteLight /> },
    { key: 'generateDraftSuggestions', label: t('Draft suggestions'), description: t('Suggest document drafts such as letters, contracts, or legal briefs.'), icon: <PiFileTextLight /> },
    { key: 'generateInsightSuggestions', label: t('Insight suggestions'), description: t('Surface strategic insights and potential risks based on the analysis.'), icon: <PiSparkleLight /> },
  ];

  const list = group === 'research' ? researchOpts : genOpts;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {list.map((opt) => (
        <WizardToggleRow
          key={opt.key}
          label={opt.label}
          description={opt.description}
          icon={opt.icon}
          enabled={opts[opt.key]}
          onToggle={() => onChange({ ...opts, [opt.key]: !opts[opt.key] })}
        />
      ))}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────

const MatterWizard: React.FC<MatterWizardProps> = ({
  initialTitle = '',
  categories,
  languages,
  jurisdictions,
  clientOptions,
  clientRoleOptions,
  dataSourceOptions,
  projectOptions = [],
  defaultLanguage,
  defaultJurisdiction,
  defaultCurrency,
  autoAddClient,
  onAddNewClient,
  onSubmit,
  onCancel,
  onBack,
  isSubmitting,
}) => {
  const pricingEnabled = isMatterEconomicsEnabled();
  const pricingCurrency = defaultCurrency || 'usd';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Wizard navigation (declared before tour so we can pass currentStep)
  const [currentStep, setCurrentStep] = useState<WizardStep>('basics');
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);

  // ── Per-step guided tour ──
  const currentTourSteps = matterWizardTourSteps[currentStep] || [];
  const {
    isRunning: isWizardTourRunning,
    handleJoyrideCallback: handleWizardTourCallback,
  } = useFeatureTour({
    tourId: `matter-wizard-tour-${currentStep}`,
    steps: currentTourSteps,
    readyToShow: currentTourSteps.length > 0,
    autoStartDelay: 600,
  });

  // Track whether any field has been touched across all steps
  const wizardDirtyRef = useRef(false);

  // Accumulated form data across steps
  const [accumulated, setAccumulated] = useState<Partial<MatterWizardData>>({
    matterTitle: initialTitle,
    category: categories[0]?.value || '',
    clients: [],
    collaborators: [],
    filesOrDescription: { files: [], description: '' },
    // No language fallback either - whichever language happened to sort first
    // is not an answer, it is a coin flip wearing an answer's clothes.
    language: defaultLanguage || '',
    // No country fallback: the user's preference or nothing at all. An
    // arbitrary first-in-the-list jurisdiction is a wrong answer wearing a
    // right answer's clothes.
    jurisdiction: defaultJurisdiction || '',
    selectedIndexes: [],
    dataSources: [],
    analysisOptions: DEFAULT_ANALYSIS_OPTIONS,
    projectUUID: '',
    ...(pricingEnabled ? { pricing: emptyMatterPricing(pricingCurrency) } : {}),
  });

  const goPrev = useCallback(() => {
    if (currentIdx > 0) {
      setCurrentStep(STEPS[currentIdx - 1].key);
    } else {
      onBack();
    }
  }, [currentIdx, onBack]);

  // Set once the user reaches Sources with no jurisdiction. It stays set, so
  // the selector does not vanish from under them the moment they pick one.
  const [askJurisdictionInSources, setAskJurisdictionInSources] = useState(false);

  // Capture form data and advance to next step
  const handleStepSubmit = useCallback((formData: Record<string, any>) => {
    setAccumulated((prev) => ({ ...prev, ...formData }));
    const jurisdiction = 'jurisdiction' in formData ? formData.jurisdiction : accumulated.jurisdiction;
    if (STEPS[currentIdx + 1]?.key === 'research' && !jurisdiction) {
      setAskJurisdictionInSources(true);
    }
    if (currentIdx < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIdx + 1].key);
    }
  }, [currentIdx, accumulated.jurisdiction]);

  // Final submit on review step
  const handleFinalSubmit = useCallback((formData: Record<string, any>) => {
    const merged = { ...accumulated, ...formData };
    onSubmit({
      matterTitle: (merged.matterTitle || '').trim(),
      category: merged.category || '',
      clients: merged.clients || [],
      collaborators: merged.collaborators || [],
      filesOrDescription: merged.filesOrDescription || { files: [], description: '' },
      language: merged.language || '',
      jurisdiction: merged.jurisdiction || '',
      selectedIndexes: merged.selectedIndexes || [],
      dataSources: merged.dataSources || [],
      analysisOptions: merged.analysisOptions || DEFAULT_ANALYSIS_OPTIONS,
      pricing: merged.pricing,
    });
  }, [accumulated, onSubmit]);

  // Sync accumulated data when BaseForm values change (keeps accumulated in sync for back navigation)
  const handleValuesChange = useCallback((values: Record<string, any>) => {
    wizardDirtyRef.current = true;
    setAccumulated((prev) => ({ ...prev, ...values }));
  }, []);

  // Wrap onCancel with a confirmation modal if the user has entered any data
  const handleCancel = useCallback(() => {
    if (wizardDirtyRef.current || currentIdx > 0) {
      // Canonical GlobalModalPayload - the legacy `{ message, subMsg }` shape
      // this used renders the headline in the body slot and logs a dev warning.
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          title: t('Discard changes'),
          message: t('You have unsaved changes. Are you sure you want to discard them?'),
          variant: 'danger',
          icon: <PiTrashLight className="w-6 h-6 text-danger-500" />,
          confirmLabel: t('Discard'),
          onConfirm: () => {
            dispatch(hideModal());
            onCancel();
          },
          onCancel: () => {
            dispatch(hideModal());
          },
        } as any)
      );
    } else {
      onCancel();
    }
  }, [currentIdx, dispatch, onCancel, t]);

  // ─── Step configs for BaseForm ─────────────────────────────────────

  const stepTitles: Record<WizardStep, { title: string; description: string }> = {
    basics: {
      title: t('Matter details'),
      description: t('Name your matter and set where it applies. Contacts and people can also be added later.'),
    },
    documents: {
      title: t('Documents'),
      description: t('Upload the documents PONS will analyze - contracts, pleadings, correspondence - or describe the matter in your own words.'),
    },
    research: {
      title: t('Sources'),
      description: t('Pick what PONS searches during analysis. Defaults follow your jurisdiction.'),
    },
    review: {
      title: t('Review and create'),
      description: t('Confirm your setup and choose which PONS features to run. Everything is enabled by default - toggle off anything you don\u2019t need.'),
    },
  };

  // Step 1: Basics - now includes language & jurisdiction
  const basicsSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'matterTitle',
          type: 'input',
          label: t('Matter title'),
          placeholder: t('e.g. Smith v. Johnson, IP License Review, Tenant Dispute...'),
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      gridCols: categories.length > 0 ? 3 : 2,
      fields: [
        ...(categories.length > 0 ? [{
          name: 'category',
          type: 'dropdown',
          label: t('Category'),
          options: categories,
          placeholder: t('Select'),
          customProps: { fullWidth: true, hierarchical: hasCategoryGroups(categories) },
          gridSize: 'third',
        } as BaseFormFieldConfig] : []),
        {
          name: 'language',
          type: 'dropdown',
          label: t('Language'),
          options: languages,
          // Defaults to the user's AI preference; overridable, and unset is a
          // valid answer - never a blocking requirement.
          placeholder: t('Select'),
          required: false,
          customProps: { fullWidth: true },
          gridSize: categories.length > 0 ? 'third' : 'half',
        } as BaseFormFieldConfig,
        {
          name: 'jurisdiction',
          type: 'dropdown',
          label: t('Jurisdiction'),
          options: jurisdictions,
          // Unset is a valid answer - the backend resolver owns the decision
          // when the user has no preference and picks nothing here.
          placeholder: t('Select jurisdiction'),
          required: false,
          customProps: { fullWidth: true },
          gridSize: categories.length > 0 ? 'third' : 'half',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Contacts'),
      description: t('Add the contacts on this matter and set each one\u2019s role, such as client.'),
      fields: [
        {
          name: 'clients',
          type: 'custom',
          // Empty label - the section heading above already says "Contacts";
          // a field label under it would only repeat it.
          label: '',

          customComponent: MatterContactsField,
          customProps: {
            clientOptions,
            roleOptions: clientRoleOptions,
            onAddNewClient,
            autoAddClient,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Access'),
      description: t('Add people who can access this matter'),
      fields: [
        {
          name: 'collaborators',
          type: 'collaboratorSelector',
          // Empty label - the section heading above already says "Access".
          label: '',

          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    ...(pricingEnabled ? [{
      title: t('Billing'),
      description: t('You can change this later from the matter.'),
      fields: [
        {
          name: 'pricing',
          type: 'custom',
          label: t('Pricing & economics'),
          customComponent: WizardBillingFields,
          customProps: { defaultCurrency: pricingCurrency },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    }] : []),
    ...(projectOptions.length > 0 ? [{
      fields: [
        {
          name: 'projectUUID',
          type: 'dropdown' as const,
          label: t('Link to project'),
          placeholder: t('Select a project (optional)'),
          required: false,
          gridSize: 'full' as const,
          customProps: { fullWidth: true },
          options: projectOptions,
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [categories, languages, jurisdictions, clientOptions, clientRoleOptions, onAddNewClient, autoAddClient, projectOptions, pricingEnabled, pricingCurrency, t]);

  const basicsSchema = useMemo(() => {
    const s = new FormSchema('WizardBasicsSchema');
    s.addField(new FormField('matterTitle', 'text', true, t('Matter title'), {
      customValidator: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Matter title is required') };
        }
        return { isValid: true, sanitized: String(value).trim() };
      },
    }));
    s.addField(new FormField('category', 'text', false, t('Category')));
    s.addField(new FormField('language', 'text', false, t('Language')));
    s.addField(new FormField('jurisdiction', 'text', false, t('Jurisdiction')));
    s.addField(new FormField('clients', 'text', false, t('Contacts')));
    s.addField(new FormField('collaborators', 'text', false, t('Access')));
    if (pricingEnabled) {
      s.addField(new FormField('pricing', 'text', false, t('Pricing & economics'), {
        customValidator: (value: any) => {
          const errs = getMatterPricingErrors(value || emptyMatterPricing(pricingCurrency));
          const firstKey = Object.keys(errs)[0] as keyof typeof errs | undefined;
          if (firstKey) return { isValid: false, error: t(errs[firstKey] as string) };
          return { isValid: true, sanitized: value };
        },
      }));
    }
    return s;
  }, [t, pricingEnabled, pricingCurrency]);

  // Step 2: Documents
  const documentsSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'filesOrDescription',
          type: 'custom',
          label: t('Files & description'),
          customComponent: FileOrDescriptionSelector,
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
  ], [t]);

  const documentsSchema = useMemo(() => {
    const s = new FormSchema('WizardDocumentsSchema');
    s.addField(new FormField('filesOrDescription', 'text', true, t('Files & description'), {
      customValidator: (value: any) => {
        const v = value || {};
        const hasFiles = v.files && v.files.length > 0;
        const hasDesc = v.description && v.description.trim().length > 0;
        if (!hasFiles && !hasDesc) {
          return { isValid: false, error: t('Upload at least one document or provide a description') };
        }
        return { isValid: true };
      },
    }));
    return s;
  }, [t]);

  // Step 3: Sources - legal indexes + data sources. One step heading and one
  // helper line say what this is; the two groups below carry short labels only.
  // Jurisdiction lives on step 1, but it is optional there - and without it
  // there are no legal sources to pick. When it was skipped, the selector
  // appears here too, so the user can set it without walking back.
  const researchSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      title: t('Legal sources'),
      fields: [
        ...(askJurisdictionInSources ? [{
          name: 'jurisdiction',
          type: 'dropdown',
          label: t('Jurisdiction'),
          options: jurisdictions,
          placeholder: t('Select jurisdiction'),
          required: false,
          customProps: { fullWidth: true },
          gridSize: 'full',
        } as BaseFormFieldConfig] : []),
        {
          name: 'selectedIndexes',
          type: 'custom',
          label: t('Search sources'),
          customComponent: IndexSourceSelector,
          customProps: {
            jurisdiction: accumulated.jurisdiction || defaultJurisdiction,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    ...(dataSourceOptions.length > 0 ? [{
      title: t('Your data sources'),
      fields: [
        {
          name: 'dataSources',
          type: 'custom',
          label: t('Data sources'),
          customComponent: DataSourceToggleList,
          customProps: { options: dataSourceOptions },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [dataSourceOptions, accumulated.jurisdiction, defaultJurisdiction, askJurisdictionInSources, jurisdictions, t]);

  const researchSchema = useMemo(() => {
    const s = new FormSchema('WizardResearchSchema');
    s.addField(new FormField('selectedIndexes', 'text', false, t('Search sources')));
    s.addField(new FormField('dataSources', 'text', false, t('Data sources')));
    if (askJurisdictionInSources) {
      s.addField(new FormField('jurisdiction', 'text', false, t('Jurisdiction')));
    }
    return s;
  }, [t, askJurisdictionInSources]);

  // Step 4: Review
  const reviewSummary = useMemo(() => ({
    title: accumulated.matterTitle || '',
    docCount: accumulated.filesOrDescription?.files?.length || 0,
    jurisdictionLabel: jurisdictions.find((j) => j.value === accumulated.jurisdiction)?.label || accumulated.jurisdiction || '',
    languageLabel: languages.find((l) => l.value === accumulated.language)?.label || accumulated.language || '',
    clientCount: accumulated.clients?.length || 0,
    collaboratorCount: accumulated.collaborators?.length || 0,
    indexCount: accumulated.selectedIndexes?.length || 0,
    dataSourceCount: accumulated.dataSources?.length || 0,
    category: categories.find((c) => c.value === accumulated.category)?.label || '',
  }), [accumulated, jurisdictions, languages, categories]);

  const reviewSections: BaseFormSectionConfig[] = useMemo(() => {
    const toggleProps = {
      jurisdiction: accumulated.jurisdiction || defaultJurisdiction,
      hasDataSources: dataSourceOptions.length > 0,
    };
    return [
      {
        title: t('Summary'),
        dataTourId: 'review-summary',
        fields: [
          {
            name: 'reviewSummary',
            type: 'custom',
            label: '',
            customComponent: ReviewSummary,
            customProps: { summary: reviewSummary, showDataSources: dataSourceOptions.length > 0 },
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Legal research'),
        dataTourId: 'review-research',
        fields: [
          {
            name: 'analysisOptions',
            type: 'custom',
            label: t('Analysis options'),
            customComponent: AnalysisOptionToggles,
            customProps: { ...toggleProps, group: 'research' },
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('PONS generation'),
        dataTourId: 'review-generation',
        fields: [
          {
            name: 'analysisOptions',
            type: 'custom',
            label: t('Analysis options'),
            customComponent: AnalysisOptionToggles,
            customProps: { ...toggleProps, group: 'generation' },
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
    ];
  }, [accumulated.jurisdiction, defaultJurisdiction, dataSourceOptions, reviewSummary, t]);

  const reviewSchema = useMemo(() => {
    const s = new FormSchema('WizardReviewSchema');
    s.addField(new FormField('analysisOptions', 'text', false, t('Analysis options')));
    return s;
  }, [t]);

  // ─── Per-step config ───────────────────────────────────────────────

  const isLastStep = currentIdx === STEPS.length - 1;

  const stepConfigs: Record<WizardStep, {
    sections: BaseFormSectionConfig[];
    schema: FormSchema;
    initialValues: Record<string, any>;
  }> = {
    basics: {
      sections: basicsSections,
      schema: basicsSchema,
      initialValues: {
        matterTitle: accumulated.matterTitle || '',
        category: accumulated.category || categories[0]?.value || '',
        language: accumulated.language || defaultLanguage || '',
        jurisdiction: accumulated.jurisdiction || defaultJurisdiction || '',
        clients: accumulated.clients || [],
        collaborators: accumulated.collaborators || [],
        projectUUID: accumulated.projectUUID || '',
      },
    },
    documents: {
      sections: documentsSections,
      schema: documentsSchema,
      initialValues: {
        filesOrDescription: accumulated.filesOrDescription || { files: [], description: '' },
      },
    },
    research: {
      sections: researchSections,
      schema: researchSchema,
      initialValues: {
        selectedIndexes: accumulated.selectedIndexes || [],
        dataSources: accumulated.dataSources || [],
        ...(askJurisdictionInSources ? { jurisdiction: accumulated.jurisdiction || '' } : {}),
      },
    },
    review: {
      sections: reviewSections,
      schema: reviewSchema,
      initialValues: {
        analysisOptions: accumulated.analysisOptions || DEFAULT_ANALYSIS_OPTIONS,
      },
    },
  };

  const config = stepConfigs[currentStep];

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <WizardStepRail
        steps={STEPS}
        currentStep={currentStep}
        maxWidth={WIZARD_MAX_WIDTH}
        data-tour-id="wizard-step-indicator"
      />
      {/* No vertical padding on the band: `WizardStepTip` renders null once
          dismissed, and a padded wrapper around nothing leaves a 16px hole on
          the steps whose tip is gone but not on the others - the body would
          shift as the user paged through. The tip owns its own `mb-4`; the
          rail above owns the gap over it. */}
      {/* Sources has no tip: its step heading and helper line already say it,
          and a tip on top read as a third heading over the same list. */}
      {currentStep !== 'research' && matterWizardStepTips[currentStep] && (
        <div className="px-6 flex-shrink-0">
          <WizardStepTip
            key={currentStep}
            tipId={`matter-wizard-${currentStep}`}
            title={matterWizardStepTips[currentStep].title}
            content={matterWizardStepTips[currentStep].content}
          />
        </div>
      )}
      <BaseForm
        key={currentStep}
        className="flex-1 min-h-0"
        title={stepTitles[currentStep].title}
        description={stepTitles[currentStep].description}
        sections={config.sections}
        schema={config.schema}
        initialValues={config.initialValues}
        onSubmit={isLastStep ? handleFinalSubmit : handleStepSubmit}
        onCancel={handleCancel}
        onBack={goPrev}
        submitLabel={isLastStep ? t('Create & analyze') : t('Next')}
        cancelLabel={t('Cancel')}
        backLabel={currentIdx === 0 ? t('Back') : t('Previous')}
        maxWidth={WIZARD_MAX_WIDTH}
        isSubmitting={isLastStep ? isSubmitting : false}
        showConfirmationModal={false}
        onValuesChange={handleValuesChange}
      />
      <FeatureTour
        steps={currentTourSteps}
        run={isWizardTourRunning}
        onCallback={handleWizardTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default MatterWizard;
